import { Input } from '@perch-labs/shrike';
import { sortBy } from 'lodash';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Button } from '~/components/button';
import {
  fetchPerchProductsForUtilityOrState,
  PerchProductOption,
} from '~/services/fetch-perch-products';
import { getPerchDemoProgramOptions } from '~/utils/constants';
import { store } from '~/utils/store';
import { ApplySuccessHint, InfoSection, InfoSectionTitle } from './dev-tools.style';

type PerchProductPickerProps = {
  utilityId: number;
  stateAbbreviation?: string;
};

export const PerchProductPicker = ({ utilityId, stateAbbreviation }: PerchProductPickerProps) => {
  const [choiceInput, setChoiceInput] = useState('');
  const [products, setProducts] = useState<PerchProductOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);

  const normalizedState = stateAbbreviation?.trim().toUpperCase();
  const programOptions = useMemo(
    () => getPerchDemoProgramOptions(stateAbbreviation),
    [stateAbbreviation],
  );
  const isProgramMode = programOptions.length > 0;
  const canQuery = utilityId > 0 || Boolean(normalizedState);

  useEffect(() => {
    if (!canQuery) {
      setProducts([]);
      setErrorMessage(null);
      setChoiceInput('');
      setApplied(false);
      return;
    }

    if (isProgramMode) {
      setProducts([]);
      setLoading(false);
      setErrorMessage(null);
      setChoiceInput('');
      setApplied(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setErrorMessage(null);
    setChoiceInput('');
    setApplied(false);

    fetchPerchProductsForUtilityOrState({
      utilityId: utilityId > 0 ? utilityId : undefined,
      stateAbbreviation: utilityId > 0 ? undefined : normalizedState,
    })
      .then(list => {
        if (!cancelled) {
          setProducts(list);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProducts([]);
          setErrorMessage('Could not load Perch products for this utility/state.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [utilityId, normalizedState, canQuery, isProgramMode]);

  const datalistSortedProducts = useMemo(() => sortBy(products, ['name']), [products]);

  const selectedProductSalesforceId = useMemo(() => {
    const match = products.find(p => p.name === choiceInput.trim());
    return match?.salesforceId ?? '';
  }, [products, choiceInput]);

  const selectedProgramId = useMemo(() => {
    const match = programOptions.find(o => o.name === choiceInput.trim());
    return match?.programId ?? '';
  }, [choiceInput, programOptions]);

  const selectedValue = isProgramMode ? selectedProgramId : selectedProductSalesforceId;

  const onApply = () => {
    if (!selectedValue) return;
    if (isProgramMode) {
      store.remove('PERCH_SF_PRODUCT_ID');
      store.set('PERCH_PROGRAM_ID', selectedValue);
    } else {
      store.remove('PERCH_PROGRAM_ID');
      store.set('PERCH_SF_PRODUCT_ID', selectedValue);
    }
    setApplied(true);
  };

  if (!canQuery) {
    return (
      <InfoSection>
        <InfoSectionTitle>Perch product / program</InfoSectionTitle>
        <p style={{ margin: 0 }}>Select a utility to load options.</p>
      </InfoSection>
    );
  }

  let mode: 'program' | 'product' = 'product';
  if (isProgramMode) {
    mode = 'program';
  }

  const modeConfig = (() => {
    switch (mode) {
      case 'program':
        return {
          sectionTitle: 'Perch program',
          listId: 'perch-program-options',
          inputName: 'perch-program-combo',
          placeholder: programOptions.map(option => option.name).join(' or '),
        };
      default:
        return {
          sectionTitle: 'Perch product',
          listId: 'perch-product-options',
          inputName: 'perch-product-combo',
          placeholder: 'Search or pick a product',
        };
    }
  })();

  const { sectionTitle, listId, inputName, placeholder } = modeConfig;

  const renderMainContent = (): ReactElement => {
    if (!isProgramMode && loading) {
      return <p style={{ margin: 0 }}>Loading products…</p>;
    }
    if (!isProgramMode && errorMessage) {
      return <p style={{ margin: 0 }}>{errorMessage}</p>;
    }
    if (!isProgramMode && products.length === 0) {
      return <p style={{ margin: 0 }}>No products found for this state.</p>;
    }

    return (
      <>
        <Input
          label={isProgramMode ? 'Program' : 'Product'}
          list={listId}
          name={inputName}
          onChange={value => {
            setChoiceInput(value);
            setApplied(false);
          }}
          placeholder={placeholder}
          value={choiceInput}
        />
        <datalist id={listId} aria-label={`${sectionTitle} suggestions`}>
          {isProgramMode
            ? programOptions.map(o => (
                <option key={o.programId} value={o.name}>
                  {o.name}
                </option>
              ))
            : datalistSortedProducts.map(p => (
                <option key={p.salesforceId} value={p.name}>
                  {p.name}
                </option>
              ))}
        </datalist>
        <Button mt={2} disabled={!selectedValue} mode="primary" onClick={onApply}>
          Apply
        </Button>
        {applied ? (
          <ApplySuccessHint role="status" aria-live="polite">
            {isProgramMode ? 'Program applied' : 'Product applied'}
          </ApplySuccessHint>
        ) : null}
      </>
    );
  };

  return (
    <InfoSection>
      <InfoSectionTitle>{sectionTitle}</InfoSectionTitle>
      {renderMainContent()}
    </InfoSection>
  );
};
